import styled from '@emotion/styled';
import Link from 'presentations/views/components/atoms/Link';
import Icon from 'presentations/views/components/atoms/Icon';
import { eventTypePath } from 'presentations/routes/path';
import {
  spacing,
  fontSize,
  fontWeight,
  mediaMax,
  borderRadius,
} from 'presentations/views/styles';
import { black, gray04 } from 'presentations/views/styles/color';
import UserEventTypeTagList from 'presentations/views/components/organisms/UserEventTypeTagList';
import { SharedEventTypeListItem } from 'presentations/api/schema/event_type';
import { useMemo } from 'react';
import Avatar, { Group } from 'presentations/views/components/atoms/Avatar';
import { UserType } from 'domains/models/event_type_user/user_type';
import { transformImageUrl } from 'presentations/assets';
import Skeleton from 'presentations/views/components/atoms/Skeleton';

interface Props {
  teamSlug: string;
  eventType: SharedEventTypeListItem;
}
export default function Component(props: Props) {
  const { owners, members } = useMemo(() => {
    const owners = [];
    const members = [];
    for (const user of props.eventType.users) {
      if (user.userType === UserType.Host) {
        owners.push(user);
      } else {
        members.push(user);
      }
    }
    return { owners, members };
  }, [props.eventType.users]);

  return (
    <Card
      href={eventTypePath({
        teamSlug: props.teamSlug,
        eventTypeSlug: props.eventType.slug,
      })}
    >
      <MainContainer>
        <Content>
          <Header>
            <UserEventTypeTagList
              duration={props.eventType.duration}
              location={props.eventType.location}
              isShowGuest
            />
          </Header>
          <EventTitle>
            <EventTitleLink>{props.eventType.name}</EventTitleLink>
          </EventTitle>
          <UserList>
            {owners.length > 0 && (
              <Invitor>
                <GroupWrap max={4}>
                  {owners.map((user) => (
                    <User
                      key={user.uid}
                      src={transformImageUrl(user.profileImage, {
                        width: 20,
                        height: 20,
                      })}
                      alt={user.name || undefined}
                      className={'MuiAvatarGroup-avatar'}
                    >
                      {user.name.charAt(0)}
                    </User>
                  ))}
                </GroupWrap>
              </Invitor>
            )}
          </UserList>
        </Content>
      </MainContainer>
      <ActionContainer>
        <Icon name="chevron-right" color="primary" size={fontSize(32)} />
      </ActionContainer>
    </Card>
  );
}

export function LoadingComponent() {
  return (
    <Card>
      <MainContainer>
        <Content>
          <Header>
            <Skeleton width={100} variant="text" />
          </Header>
          <EventTitle>
            <Skeleton width={240} variant="text" />
          </EventTitle>
          <UserList>
            <Invitor>
              <Skeleton width={20} height={20} variant="circular" />
            </Invitor>
          </UserList>
        </Content>
      </MainContainer>
    </Card>
  );
}

const Card = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${spacing(32)};
  border: 1px solid ${gray04};
  border-radius: ${borderRadius(16)};
  color: ${black};
`;
const MainContainer = styled.div`
  display: flex;
`;
const ActionContainer = styled.div``;
const Content = styled.div`
  width: 100%;
`;
const Header = styled.div`
  margin-bottom: ${spacing(4)};
`;
const EventTitle = styled.div`
  font-size: ${fontSize(20)};
  font-weight: ${fontWeight('bold')};
`;
const EventTitleLink = styled.div``;

const UserList = styled.div`
  display: flex;
  align-items: center;
  margin-top: ${spacing(8)};
`;
const Invitor = styled.div`
  margin-right: ${spacing(24)};
  display: flex;
  align-items: center;
  ${mediaMax.tablet} {
    margin-right: ${spacing(16)};
  }
`;
const GroupWrap = styled(Group)`
  .MuiAvatar-root {
    width: 20px;
    height: 20px;
    font-size: ${fontSize(14)};
    font-weight: bold;
  }
`;
const User = styled(Avatar)`
  width: 20px;
  height: 20px;
  font-weight: ${fontWeight('bold')};

  .MuiAvatarGroup-avatar:first-of-type {
    margin-left: 0;
  }
`;
